import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { RunProgressNotification } from "@shared/types/runs";
import { useFeedTransport, useRunFeed, type ProgressTransport } from "@/lib/live/InstanceRunFeed";
import { isLoadFrame } from "@/lib/runs/runActivity";

/**
 * Live run progress over the pipeline-status WebSocket (FR-55).
 *
 * Session 10 (P12): the socket moved to one ref-counted feed per tab
 * (`lib/live/InstanceRunFeed.tsx`), so a run page, an editor panel and a list of strips share
 * ONE socket. This hook is now a runId-filtered consumer of that feed; its API and its
 * behaviour are unchanged.
 *
 * **Kept from the donor.** Exponential backoff at `min(1000 · 2^n, 30000)` with the attempt
 * counter reset on a successful open (now in the feed), and the stale-run clearing: a message
 * for a different run never merges into the rows on screen.
 *
 * **The token travels in `Sec-WebSocket-Protocol`**, never the query string, which is written
 * verbatim into every ingress access log on the path. The hook reports its transport state,
 * because the page above it has to fall back to polling the moment the socket is not carrying
 * messages: a live indicator that only ever says "Live" is how a frozen page gets shipped.
 *
 * The stale-run clearing is a DERIVATION, not an effect. State carries the run it belongs to
 * and a mismatch resolves to the empty state during render, so navigating from one run to
 * another cannot show the first run's entities under the second run's heading even for a frame.
 *
 * What this hook is NOT is a source of truth. `NOTIFY` reaches only the sessions connected at
 * that moment, so a message can be missed; the rows are in the database and `GET /runs/:runId`
 * returns them. Progress here is a hint that saves a request, never the record.
 *
 * Load frames (`LOAD_*`, S1) and step frames carry no extract count: they reach `batch`
 * but never the per-table map, which holds the extract's rows only. #1465: the page folds every
 * frame of `batch`, so coalescing the state updates drops no frame.
 */

export {
  RECONNECT_BASE_MS,
  RECONNECT_MAX_MS,
  WS_BEARER_PREFIX,
  WS_PATH,
  WS_SUBPROTOCOL,
  backoffDelayMs,
  progressSocketUrl,
  type ProgressTransport,
} from "@/lib/live/InstanceRunFeed";

export interface RunProgressState {
  transport: ProgressTransport;
  /** True only while the socket is open; the page polls whenever it is false. */
  isLive: boolean;
  /** The latest notification per entity, newest wins. */
  entities: ReadonlyMap<string, RunProgressNotification>;
  /** The latest notification of any kind, for the run-level live region. */
  lastEvent: RunProgressNotification | null;
  /**
   * Every frame of the latest flush, in arrival order, as a new array per flush. The run page
   * folds each one: load outcomes, step frames and run frames reach the screen only this way.
   */
  batch: readonly RunProgressNotification[];
  /** How many times the socket has re-dialled since it last opened. */
  reconnectAttempts: number;
}

/** State plus the run it describes, so a mismatch can be resolved by deriving. */
interface KeyedProgress {
  runId: string | null;
  entities: ReadonlyMap<string, RunProgressNotification>;
  lastEvent: RunProgressNotification | null;
  batch: readonly RunProgressNotification[];
}

/** One entity of one connection. A name alone collides across connections. */
export function entityKey(dataflowId: string | null, entityName: string): string {
  return `${dataflowId ?? ""}::${entityName}`;
}

/** How often buffered frames are merged into the rows on screen. */
export const PROGRESS_FLUSH_MS = 1_000;

const TERMINAL_RUN_STATUSES: ReadonlySet<string> = new Set([
  "succeeded",
  "failed",
  "partially_succeeded",
  "cancelled",
]);

const NO_ENTITIES: ReadonlyMap<string, RunProgressNotification> = new Map();
const NO_BATCH: readonly RunProgressNotification[] = [];

/** Disabled until a run is followed; "connecting" while the feed itself is still disabled. */
function progressTransport(connecting: boolean, feed: ProgressTransport): ProgressTransport {
  if (!connecting) return "disabled";
  return feed === "disabled" ? "connecting" : feed;
}

function emptyFor(runId: string | null): KeyedProgress {
  return { runId, entities: NO_ENTITIES, lastEvent: null, batch: NO_BATCH };
}

/** An entity in one of these has finished; a later message cannot un-finish it. */
const SETTLED_STATUSES = new Set(["succeeded", "failed", "skipped"]);

/**
 * Two notifications for one entity, folded so the row never moves backwards.
 *
 * `NOTIFY` carries no ordering guarantee across sessions, so a message written earlier can
 * arrive after one written later. The count keeps its high-water mark and a settled status is
 * never replaced.
 */
export function foldNotification(
  previous: RunProgressNotification | undefined,
  next: RunProgressNotification,
): RunProgressNotification {
  if (!previous) return next;
  const settled = previous.status !== null && SETTLED_STATUSES.has(previous.status);
  return {
    ...next,
    records: Math.max(previous.records ?? 0, next.records ?? 0),
    status: settled ? previous.status : (next.status ?? previous.status),
    error: next.error ?? previous.error,
  };
}

export interface UseRunProgressOptions {
  runId: string | null;
  instanceId: string | null;
  /** False while the run is terminal: a finished run has nothing left to push. */
  enabled?: boolean;
}

export function useRunProgress({
  runId,
  instanceId,
  enabled = true,
}: UseRunProgressOptions): RunProgressState {
  const connecting = Boolean(enabled && runId && instanceId);
  const feed = useRunFeed(instanceId);
  const feedTransport = useFeedTransport(connecting ? feed : null);

  const [state, setState] = useState<KeyedProgress>(() => emptyFor(runId));

  /**
   * Every write goes through here so no handler can resurrect a previous run's state: a
   * callback that fires after the screen has moved on names a run the current one is not, and
   * its update is dropped rather than merged.
   */
  const updateFor = useCallback(
    (forRunId: string, patch: (previous: KeyedProgress) => Partial<KeyedProgress>) => {
      setState((previous) => {
        const base = previous.runId === forRunId ? previous : emptyFor(forRunId);
        return { ...base, ...patch(base) };
      });
    },
    [],
  );

  // #1465: a 6,000-table import sends frames faster than the page needs to paint them. They are
  // buffered and merged once per second with one setState, and a frame that ends a run flushes
  // at once so the page never shows a finished run as still moving.
  const buffer = useRef<RunProgressNotification[]>([]);

  useEffect(() => {
    if (!enabled || !runId || !feed) return;
    const forRunId = runId;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const flush = () => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      const frames = buffer.current;
      if (frames.length === 0) return;
      buffer.current = [];
      updateFor(forRunId, (previous) => {
        let entities = previous.entities;
        let copied = false;
        for (const notification of frames) {
          if (!notification.entityName || notification.step || isLoadFrame(notification)) continue;
          if (!copied) {
            entities = new Map(previous.entities);
            copied = true;
          }
          const key = entityKey(notification.dataflowId, notification.entityName);
          (entities as Map<string, RunProgressNotification>).set(
            key,
            foldNotification(entities.get(key), notification),
          );
        }
        return {
          lastEvent: frames[frames.length - 1] ?? previous.lastEvent,
          batch: frames,
          entities,
        };
      });
    };
    buffer.current = [];
    const off = feed.onFrame((notification) => {
      // Stale-run clearing: the socket is per instance, so another run's progress arrives
      // here too and must never land in this run's rows.
      if (notification.runId !== forRunId) return;
      buffer.current.push(notification);
      if (notification.runStatus && TERMINAL_RUN_STATUSES.has(notification.runStatus)) {
        flush();
        return;
      }
      timer ??= setTimeout(flush, PROGRESS_FLUSH_MS);
    });
    const release = feed.acquire();
    return () => {
      off();
      release();
      if (timer !== null) clearTimeout(timer);
      buffer.current = [];
    };
  }, [runId, enabled, feed, updateFor]);

  // The derivation that makes the stale-run clearing structural.
  const current = state.runId === runId ? state : emptyFor(runId);
  const transport = progressTransport(connecting, feedTransport.transport);
  const reconnectAttempts = connecting ? feedTransport.reconnectAttempts : 0;

  return useMemo(
    () => ({
      transport,
      isLive: transport === "live",
      entities: current.entities,
      lastEvent: current.lastEvent,
      batch: current.batch,
      reconnectAttempts,
    }),
    [transport, current.entities, current.lastEvent, current.batch, reconnectAttempts],
  );
}
